import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { BeamWidth, Placement } from '@shure/shared/angular/data-access/system-api/models';

import {
	CoveragePerspectives,
	HeightDiff,
	LimitsOfAngle,
	LimitsOfHeight,
	LobeAngle,
	Mxa7xxLookupMap,
	Mxa7xxLookupTableData,
	Mxa7xxLookupTableLoader,
	ValueLimits
} from './mxa7xx-lookup-table-loader.api';
import { Mxa7xxModel } from './mxa7xx-model';

// Maps BeamWidth values to CSV filename prefixes.
const beamWidthRecord: Record<BeamWidth, string> = {
	[BeamWidth.Narrow]: 'Narrow',
	[BeamWidth.Medium]: 'Medium',
	[BeamWidth.Wide]: 'Wide'
};

// Maps Placement values to folder names in assets.
const placementRecord: Record<Placement, string> = {
	[Placement.Ceiling]: 'ceiling',
	[Placement.Table]: 'table',
	[Placement.WallHorizontal]: 'wall-horizontal',
	[Placement.WallVertical]: 'wall-vertical',
	[Placement.NotConfigured]: 'not-configured'
};

// Maps CoveragePerspectives to folder names in assets.
const coveragePerspectiveRecord: Record<CoveragePerspectives, string> = {
	[CoveragePerspectives.TopDown]: 'top-down',
	[CoveragePerspectives.Side]: 'side'
};

export abstract class Mxa7xxBaseLookupTableLoaderService implements Mxa7xxLookupTableLoader {
	private readonly http: HttpClient = inject(HttpClient);

	private _lookupMap: Mxa7xxLookupMap = new Map();
	private _valueLimits: ValueLimits = {
		limitsAngle: { minAngle: 0, maxAngle: 0 },
		limitsHeight: { minHeight: 0, maxHeight: 0 }
	};
	private isDataLoaded = false;

	public abstract readonly mxa7xxModel: Mxa7xxModel;
	protected abstract readonly lookupDataPath: string;

	private readonly beamWidths = Object.values(BeamWidth);

	protected abstract readonly supportedTables: Map<CoveragePerspectives, Placement[]>;

	public get lookupMap(): Mxa7xxLookupMap {
		return this._lookupMap;
	}

	public get valueLimits(): ValueLimits {
		return this._valueLimits;
	}

	public async load(): Promise<boolean> {
		if (this.isDataLoaded) {
			return true;
		}

		const entries: [CoveragePerspectives, Placement[]][] = Array.from(this.supportedTables.entries());

		for (const [perspective, installations] of entries) {
			const perspectiveStr = coveragePerspectiveRecord[perspective];
			const installationMap: Map<
				Placement,
				Map<BeamWidth, Map<HeightDiff, Map<LobeAngle, Mxa7xxLookupTableData>>>
			> = new Map();

			for (const installation of installations) {
				const installationStr = placementRecord[installation];
				const beamMap: Map<BeamWidth, Map<HeightDiff, Map<LobeAngle, Mxa7xxLookupTableData>>> = new Map();

				for (const beamWidth of this.beamWidths) {
					const beamWidthStr = beamWidthRecord[beamWidth];
					const [angle, cx, cy, rx, ry] = await Promise.all([
						this.loadCsvFile(`${perspectiveStr}/${installationStr}/${beamWidthStr}-angle-table.csv`),
						this.loadCsvFile(`${perspectiveStr}/${installationStr}/${beamWidthStr}-cx-table.csv`),
						this.loadCsvFile(`${perspectiveStr}/${installationStr}/${beamWidthStr}-cy-table.csv`),
						this.loadCsvFile(`${perspectiveStr}/${installationStr}/${beamWidthStr}-rx-table.csv`),
						this.loadCsvFile(`${perspectiveStr}/${installationStr}/${beamWidthStr}-ry-table.csv`)
					]);

					const heightMap = this.addValuesForBeamWidth(angle, cx, cy, rx, ry);

					beamMap.set(beamWidth, heightMap);
				}
				installationMap.set(installation, beamMap);
			}
			this._lookupMap.set(perspective, installationMap);
		}

		this.isDataLoaded = true;

		return true;
	}

	private loadCsvFile(fileName: string): Promise<string> {
		return firstValueFrom(this.http.get(`${this.lookupDataPath}/${fileName}`, { responseType: 'text' }));
	}

	private addValuesForBeamWidth(
		angleData: string,
		cxData: string,
		cyData: string,
		rxData: string,
		ryData: string
	): Map<HeightDiff, Map<LobeAngle, Mxa7xxLookupTableData>> {
		this._valueLimits.limitsAngle = this.getAngleKeysMinMax(angleData);

		const angleDataStripAngleheaders = this.removeHorizontalHeaders(angleData);
		const cxDataStripAngleheaders = this.removeHorizontalHeaders(cxData);
		const cyDataStripAngleheaders = this.removeHorizontalHeaders(cyData);
		const rxDataStripAngleheaders = this.removeHorizontalHeaders(rxData);
		const ryDataStripAngleHeaders = this.removeHorizontalHeaders(ryData);

		const heightKeys = this.getHeightKeys(angleDataStripAngleheaders);

		this._valueLimits.limitsHeight = this.getHeightMinMax(heightKeys);

		const angleDataStripped = this.stripHeightKeys(angleDataStripAngleheaders);
		const cxDataStripped = this.stripHeightKeys(cxDataStripAngleheaders);
		const cyDataStripped = this.stripHeightKeys(cyDataStripAngleheaders);
		const rxDataStripped = this.stripHeightKeys(rxDataStripAngleheaders);
		const ryDataStripped = this.stripHeightKeys(ryDataStripAngleHeaders);

		const angleDataArray = angleDataStripped.map((v) => v.split(','));
		const cxDataArray = cxDataStripped.map((v) => v.split(','));
		const cyDataArray = cyDataStripped.map((v) => v.split(','));
		const rxDataArray = rxDataStripped.map((v) => v.split(','));
		const ryDataArray = ryDataStripped.map((v) => v.split(','));

		const middleMap: Map<HeightDiff, Map<LobeAngle, Mxa7xxLookupTableData>> = new Map();

		const lastIndexAngle = angleDataArray[0].length - 1;

		for (let heightIdx = 0; heightIdx < heightKeys.length; heightIdx++) {
			const innerMap: Map<LobeAngle, Mxa7xxLookupTableData> = new Map();

			for (let angle = 0; angle <= lastIndexAngle; angle++) {
				innerMap.set(angle, {
					angle: Number.parseFloat(angleDataArray[heightIdx][angle]),
					cx: Number.parseFloat(cxDataArray[heightIdx][angle]),
					cy: Number.parseFloat(cyDataArray[heightIdx][angle]),
					rx: Number.parseFloat(rxDataArray[heightIdx][angle]),
					ry: Number.parseFloat(ryDataArray[heightIdx][angle])
				});
			}

			middleMap.set(Number.parseFloat(heightKeys[heightIdx]), innerMap);
		}
		return middleMap;
	}

	private getHeightKeys(dataStrippedOfHheaders: string): Array<string> {
		const data = dataStrippedOfHheaders.replace(/[^0-9.,\r\n]/g, '');

		return data.split('\r\n').map((ele) => ele.substring(0, ele.indexOf(',')));
	}

	private stripHeightKeys(dataStrippedOfAngleHeaders: string): string[] {
		return dataStrippedOfAngleHeaders.split('\r\n').map((ele) => ele.substring(ele.indexOf(',') + 1));
	}

	private getHeightMinMax(data: string[]): LimitsOfHeight {
		const dataParsedToNumber = data.map(Number);
		return { minHeight: Math.min(...dataParsedToNumber), maxHeight: Math.max(...dataParsedToNumber) };
	}

	private getAngleKeysMinMax(dataString: string): LimitsOfAngle {
		const angleHeaderRow = dataString.substring(0, dataString.indexOf('\r\n'));

		const tempAngleArray = angleHeaderRow
			.replace(/[^0-9.,\r\n]/g, '')
			.split(',')
			.map(Number);

		return { minAngle: Math.min(...tempAngleArray), maxAngle: Math.max(...tempAngleArray) };
	}

	private removeHorizontalHeaders(data: string): string {
		const endOfRow = '\r\n';
		const endOfRowIndex = data.indexOf(endOfRow);
		return data.substring(endOfRowIndex + endOfRow.length);
	}
}
